import { useRouter } from 'next/router';
import type { FC } from 'react';
import { memo, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ButtonV3Icon } from '../../components/Button/ButtonV3Icon';
import { Tooltip } from '../../components/GraphModal/Tooltip';
import type { LinkProps } from '../../components/Link';
import { Link } from '../../components/Link';
import { ReactComponent as BubbleflowIcon } from '../../icons/icon-bubbleflow.svg';
import type { Message } from '../../types/api';

const intl = defineMessages({
  bubbleFlowView: { defaultMessage: 'Vue BubbleFlow', id: 'IQvkPD' },
});

interface BubbleFlowButtonProps {
  message: Message;
  linkProps?: Partial<LinkProps>;
  shouldIndex?: boolean;
}

export const BubbleFlowButton: FC<BubbleFlowButtonProps> = memo(
  ({ message, linkProps, shouldIndex }) => {
    const { query } = useRouter();

    const { slug, back } = useMemo(
      () =>
        typeof linkProps?.params === 'object'
          ? ((linkProps?.params ?? query ?? {}) as Record<string, string>)
          : {
              slug: message.thread?.topic?.slug ?? (query.slug as string),
              back: false,
            },
      [linkProps, message, query],
    );

    const href = useMemo(
      () => ({
        to: 'message',
        params: {
          slug,
          messageId: message?.id,
          back,
        },
      }),
      [slug, back, message],
    );

    return (
      <Link {...href} passHref>
        <a>
          <Tooltip content={<FormattedMessage {...intl.bubbleFlowView} />}>
            <ButtonV3Icon
              variant="outlined"
              radius="rounded-full"
              icon={<BubbleflowIcon className="w-8 h-8" />}
              className="BubbleFlowButton [&:not(:hover)]:border-transparent h-[46px] w-[46px] overflow-hidden text-gray-480/90 hover:text-gray-500/90"
              linkDisableIndex={!shouldIndex}
            />
          </Tooltip>
        </a>
      </Link>
    );
  },
  (prev, next) => prev.message.id === next.message.id,
);
